/**
 * ============================================================================
 * SOURCE CONTEXT PANEL
 * ============================================================================
 *
 * A floating panel that appears below text fields when editing in a
 * non-source language. Provides context to translators by showing the
 * original source text and (in the future) additional translation context.
 *
 * Architecture:
 *   <SourceContextPanel>         — container with animation/visibility
 *     <SourceTextSection />      — shows the source text
 *     <ContextSection />         — (future) shows string context/notes
 *     <SuggestionsSection />     — (future) shows MT suggestions
 *   </SourceContextPanel>
 *
 * @module source-context-panel
 */

import React from 'react';
import { IconProvider } from '@/components/ui';
import type {
  SourceContextPanelProps,
  SourceTextSectionProps,
} from './types';

// ============================================================================
// PANEL CONTAINER
// ============================================================================

/**
 * Container for the source context panel.
 * Handles visibility and provides a consistent visual wrapper.
 * Children should be section components (SourceTextSection, etc.)
 */
export const SourceContextPanel: React.FC<SourceContextPanelProps> = ({
  isVisible,
  children,
}) => {
  if (!isVisible) return null;

  return (
    <div
      className="
        mt-4 rounded border border-border/60 bg-muted/30 
        animate-in fade-in slide-in-from-top-1 duration-150
        overflow-hidden
      "
      role="complementary"
      aria-label="Source text reference"
    >
      {children}
    </div>
  );
};

// ============================================================================
// SOURCE TEXT SECTION
// ============================================================================

/**
 * Displays the source (default language) text for reference while translating.
 * Renders HTML content safely since source text may contain formatting.
 */
export const SourceTextSection: React.FC<SourceTextSectionProps> = ({
  sourceText,
  locaKey,
}) => {
  return (
    <div className="px-3 py-2 text-xs">
      {/* Section header */}
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        <IconProvider iconstring="fa-languageslucide" size={14} />
        <span className="font-medium uppercase tracking-wide text-[10px]">
          Source Text
        </span>
        {locaKey && (
          <span className="ml-auto font-mono text-[10px] text-muted-foreground/70 flex items-center gap-1">
            <IconProvider iconstring="fa-link" size={9} />
            {locaKey}
          </span>
        )}
      </div>

      {/* Source text content */}
      <div
        className="text-foreground/80 whitespace-pre-wrap break-words leading-relaxed"
        dangerouslySetInnerHTML={{ __html: sourceText || '' }}
      />
    </div>
  );
};
